import OrgFreezeControl from '@/components/OrgFreezeControl'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { he } from 'date-fns/locale'
import { formatInTimeZone } from 'date-fns-tz'
import { RefreshCw, ShieldAlert } from 'lucide-react'
import {
  orgPulseApi,
  OrgPulseForbiddenError,
  type LastOutreach,
  type OrgPulseSnapshot,
  type PulseCounts,
  type PulseOrg,
} from '@/api/orgPulse'
import { formatNIS } from '@/utils/currency'
import TrialManagementView from '@/components/TrialManagementView'

type PulseFilter = 'all' | 'trial' | 'dead' | 'nodate' | 'paid' | 'quiet' | 'call'

const ZONE = 'Asia/Jerusalem'
const CARD = { background: '#1a1d27', border: '1px solid #2a2d3e' } as const
const MUTED = '#9ca3af'
const LINE = '#2a2d3e'

const STATE_LABEL: Record<string, { label: string; tone: ChipTone }> = {
  trial: { label: 'בהתנסות', tone: 'info' },
  paid: { label: 'משלם', tone: 'ok' },
  comp: { label: 'הטבה', tone: 'idle' },
  demo: { label: 'דמו', tone: 'idle' },
  expired: { label: 'פג תוקף', tone: 'idle' },
  suspended: { label: 'מושהה', tone: 'warn' },
}

const HANDLER_LABEL: Record<string, { label: string; tone: ChipTone }> = {
  system: { label: 'המערכת', tone: 'info' },
  nizan: { label: 'אתה', tone: 'bad' },
  nobody: { label: 'אין צורך', tone: 'idle' },
}

const CHANGE_KEYS = ['contacts', 'deals', 'tasks', 'activities', 'messages', 'documents'] as const
type ChangeKey = (typeof CHANGE_KEYS)[number]

const CHANGE_LABEL: Record<ChangeKey, string> = {
  contacts: 'לקוחות',
  deals: 'עסקאות',
  tasks: 'משימות',
  activities: 'פעילות',
  messages: 'הודעות',
  documents: 'מסמכים',
}

const ACTOR_LABEL: Record<string, string> = {
  human: 'אדם',
  system: 'מערכת',
}

const CHANNEL_LABEL: Record<string, string> = {
  whatsapp: 'וואטסאפ',
  wa: 'וואטסאפ',
  email: 'מייל',
  sms: 'מסרון',
}

const SENDER_LABEL: Record<string, string> = {
  system: 'המערכת',
  human: 'אתה',
  nizan: 'אתה',
  admin: 'אתה',
}

const SEVERITY_BAR: Record<string, string> = {
  red: '#f87171',
  amber: '#fbbf24',
  green: '#2dd4bf',
  grey: '#6b7280',
}

type ChipTone = 'ok' | 'warn' | 'bad' | 'info' | 'idle'

const CHIP: Record<ChipTone, { color: string; background: string }> = {
  ok: { color: '#2dd4bf', background: '#12291C' },
  warn: { color: '#fbbf24', background: '#2E2413' },
  bad: { color: '#f87171', background: '#321719' },
  info: { color: '#a5b4fc', background: '#1B2A48' },
  idle: { color: '#9ca3af', background: '#232A35' },
}

const FILTERS: Array<{ id: PulseFilter; label: string }> = [
  { id: 'all', label: 'הכל' },
  { id: 'trial', label: 'התנסות' },
  { id: 'paid', label: 'משלמים' },
  { id: 'call', label: 'צריך לפנות' },
  { id: 'quiet', label: 'שקטים 24ש' },
]

function localStamp(iso: string): string {
  try {
    return formatInTimeZone(parseISO(iso), ZONE, 'EEE d.M HH:mm', { locale: he })
  } catch {
    return iso
  }
}

function windowLabel(snapshot: OrgPulseSnapshot): string {
  const edge = snapshot.window_end_inclusive ? 'כולל' : 'הסוף לא כלול'
  return `חלון: ${localStamp(snapshot.window_start)} – ${localStamp(snapshot.window_end)} (${edge})`
}

function matches(org: PulseOrg, filter: PulseFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'trial') return org.subscription_state === 'trial'
  if (filter === 'paid') return org.subscription_state === 'paid'
  if (filter === 'dead') return org.subscription_state === 'trial' && !org.last_login_at
  if (filter === 'nodate') return org.subscription_state === 'trial' && org.trial_no_end_date
  if (filter === 'quiet') return org.subscription_state === 'paid' && org.human.total === 0
  return org.handler === 'nizan'
}

function planText(org: PulseOrg): string {
  const name = org.plan_name || org.plan_code || '—'
  if (org.price_agorot > 0) return `${name} ${formatNIS(org.price_agorot)}`
  return name
}

function outreachText(outreach: LastOutreach | null): string {
  if (!outreach) return '—'
  const channel = CHANNEL_LABEL[outreach.channel] ?? (outreach.channel || 'הודעה')
  const sender = SENDER_LABEL[outreach.sender] ?? (outreach.sender || '')
  const who = sender ? ` · ${sender}` : ''
  const code = outreach.failure_code != null && outreach.failure_code !== ''
    ? ` · ${outreach.failure_code}`
    : ''
  if (outreach.delivered && !code) return `נמסר · ${channel}${who}`
  return `לא נמסר${code} · ${channel}${who}`
}

function waHref(phone: string | null): string | null {
  if (!phone) return null
  const digits = phone.replace(/\D/g, '')
  if (!digits) return null
  const intl = digits.startsWith('0') ? `972${digits.slice(1)}` : digits
  return `https://wa.me/${intl}`
}

function changeLabel(key: string): string {
  return CHANGE_KEYS.includes(key as ChangeKey) ? CHANGE_LABEL[key as ChangeKey] : key
}

function countOf(predicate: (org: PulseOrg) => boolean, orgs: PulseOrg[]): number {
  return orgs.reduce((sum, org) => sum + (predicate(org) ? 1 : 0), 0)
}

function Chip({ tone, children }: { tone: ChipTone; children: string }) {
  const style = CHIP[tone]
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
      style={style}
    >
      {children}
    </span>
  )
}

function ChangeChips({ counts, emphasizeZero }: { counts: PulseCounts; emphasizeZero?: boolean }) {
  const parts = CHANGE_KEYS.filter((key) => counts[key] > 0)
  if (parts.length === 0) {
    return (
      <span className={emphasizeZero ? 'font-semibold text-red-400' : ''} style={{ color: emphasizeZero ? undefined : MUTED }}>
        {emphasizeZero ? '0 שינויים' : '0'}
      </span>
    )
  }
  return (
    <div className="flex flex-wrap gap-1">
      {parts.map((key) => (
        <span
          key={key}
          className="rounded-md px-1.5 py-0.5 text-[11.5px]"
          style={{ border: `1px solid ${LINE}`, color: MUTED }}
        >
          {CHANGE_LABEL[key]} <b className="text-white">{counts[key]}</b>
        </span>
      ))}
    </div>
  )
}

function DaysLeft({ org }: { org: PulseOrg }) {
  if (org.subscription_state !== 'trial') return <span style={{ color: MUTED }}>—</span>
  if (org.trial_no_end_date || org.trial_days_left == null) {
    return <Chip tone="warn">אין תאריך סיום</Chip>
  }
  const days = org.trial_days_left
  const width = Math.max(4, Math.min(100, Math.round((days / 30) * 100)))
  return (
    <div className="grid min-w-[92px] gap-1">
      <span>{days} ימים</span>
      <div className="h-1.5 overflow-hidden rounded-full" style={{ background: '#2A3445' }}>
        <div
          className="h-full"
          style={{ width: `${width}%`, background: days <= 3 ? '#fbbf24' : '#6366f1' }}
        />
      </div>
    </div>
  )
}

function PulseDrawer({ org }: { org: PulseOrg }) {
  const state = STATE_LABEL[org.subscription_state] ?? { label: org.subscription_state, tone: 'idle' as const }
  const href = waHref(org.owner_phone)
  const step = org.next_journey_step
  return (
    <section
      className="grid gap-3 rounded-xl p-3.5"
      style={CARD}
      aria-live="polite"
      data-drawer-org={org.name}
    >
      <h3 className="flex flex-wrap items-center gap-2 text-base font-semibold text-white">
        {org.name}
        <Chip tone={state.tone}>{state.label}</Chip>
      </h3>
      <div className="grid gap-3 md:grid-cols-3">
        <div className="grid gap-1.5 rounded-lg p-2.5 text-sm" style={{ border: `1px solid ${LINE}` }}>
          <h4 className="text-xs font-medium" style={{ color: MUTED }}>מצב</h4>
          <div>מסלול: {planText(org)}</div>
          <div>כניסה אחרונה: {org.last_login_at ? localStamp(org.last_login_at) : 'לא נכנס אף פעם'}</div>
          <div>
            נשאר: {org.subscription_state !== 'trial'
              ? '—'
              : org.trial_no_end_date || org.trial_days_left == null
                ? 'אין תאריך סיום'
                : `${org.trial_days_left} ימים`}
          </div>
          <div>בעלים: {org.owner_name ?? '—'}{org.owner_phone ? ` · ${org.owner_phone}` : ''}</div>
          <div>פנייה אחרונה: {outreachText(org.last_outreach)}</div>
        </div>
        <div className="grid content-start gap-1.5 rounded-lg p-2.5 text-sm" style={{ border: `1px solid ${LINE}` }}>
          <h4 className="text-xs font-medium" style={{ color: MUTED }}>שינויים בחלון (אנשים בלבד)</h4>
          <div>אדם {org.human.total}</div>
          <ChangeChips counts={org.human} emphasizeZero={org.human.total === 0} />
          <div className="text-xs" style={{ color: MUTED }}>
            שינויים של המערכת: {org.system.total}
          </div>
          <ChangeChips counts={org.system} />
        </div>
        <div className="grid content-start gap-1.5 rounded-lg p-2.5 text-sm" style={{ border: `1px solid ${LINE}` }}>
          <h4 className="text-xs font-medium" style={{ color: MUTED }}>ציר זמן</h4>
          {org.timeline.length === 0 ? (
            <p style={{ color: MUTED }}>אין שינויים בחלון</p>
          ) : (
            <ul className="grid gap-1.5">
              {org.timeline.slice(0, 8).map((event) => (
                <li key={`${event.at}-${event.entity_id ?? event.source}`} className="grid grid-cols-[4.5rem_1fr] gap-2">
                  <time className="text-xs tabular-nums" style={{ color: MUTED }}>{localStamp(event.at)}</time>
                  <span>
                    {changeLabel(event.change_type)}
                    {' · '}
                    {ACTOR_LABEL[event.actor_kind] ?? event.actor_kind}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {org.handler === 'nizan' ? (
        <div className="flex flex-wrap gap-2">
          {href ? (
            <a
              className="rounded-lg bg-indigo-500 px-2.5 py-1.5 text-xs text-white"
              href={href}
              target="_blank"
              rel="noreferrer"
            >
              שלח וואטסאפ לבעלים
            </a>
          ) : (
            <span className="text-xs" style={{ color: MUTED }}>אין מספר של הבעלים</span>
          )}
          <span className="rounded-lg px-2.5 py-1.5 text-xs" style={{ border: `1px solid ${LINE}`, color: MUTED }}>
            המסך לקריאה בלבד
          </span>
        </div>
      ) : (
        <p className="text-sm" style={{ color: MUTED }}>
          {org.handler === 'system'
            ? `המסע יטפל.${step?.label ? ` הצעד הבא: ${step.label}` : ''}${step?.scheduled_for ? ` · ${localStamp(step.scheduled_for)}` : ''}`
            : 'אין פעולה נדרשת כרגע.'}
        </p>
      )}
    </section>
  )
}

export default function OrgPulsePage() {
  const [view, setView] = useState<'pulse' | 'trials'>('pulse')
  const [includeTest, setIncludeTest] = useState(false)
  const [filter, setFilter] = useState<PulseFilter>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['org-pulse', includeTest],
    queryFn: () => orgPulseApi.snapshot(includeTest),
    retry: false,
  })

  const orgs = query.data?.orgs ?? []
  const visible = useMemo(() => orgs.filter((org) => matches(org, filter)), [orgs, filter])
  const selected = visible.find((org) => org.organization_id === selectedId) ?? visible[0] ?? null

  const kpis = useMemo(() => ([
    { id: 'trial' as const, label: 'בהתנסות', tone: 'plain' as const, n: countOf((org) => matches(org, 'trial'), orgs) },
    { id: 'dead' as const, label: 'בהתנסות ולא נכנסו אף פעם', tone: 'bad' as const, n: countOf((org) => matches(org, 'dead'), orgs) },
    { id: 'nodate' as const, label: 'התנסות בלי תאריך סיום', tone: 'warn' as const, n: countOf((org) => matches(org, 'nodate'), orgs) },
    { id: 'paid' as const, label: 'משלמים', tone: 'plain' as const, n: countOf((org) => matches(org, 'paid'), orgs) },
    { id: 'quiet' as const, label: 'משלמים בלי שינוי ב־24ש', tone: 'bad' as const, n: countOf((org) => matches(org, 'quiet'), orgs) },
    { id: 'call' as const, label: 'דורשים פנייה ממך', tone: 'plain' as const, n: countOf((org) => matches(org, 'call'), orgs) },
  ]), [orgs])

  if (query.isError && query.error instanceof OrgPulseForbiddenError) {
    return (
      <div dir="rtl" className="mx-auto grid max-w-3xl justify-items-center gap-2 rounded-xl p-8 text-center" style={CARD}>
        <ShieldAlert size={28} className="text-amber-400" />
        <h1 className="text-lg font-semibold text-white">אין הרשאה</h1>
        <p className="text-sm" style={{ color: MUTED }}>
          הדופק זמין רק למשתמשי ניהול. הבקשה נדחתה (42501).
        </p>
      </div>
    )
  }

  return (
    <div dir="rtl" className="mx-auto grid w-full min-w-0 max-w-[1180px] gap-4 overflow-x-hidden">
      <div className="grid min-w-0 gap-3.5 rounded-2xl p-4" style={CARD}>
        <div className="flex gap-2" role="tablist" aria-label="תצוגת דופק ארגונים">
          {([['pulse', 'דופק ארגונים'], ['trials', 'ניהול התנסויות']] as const).map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => setView(id)}
              className="rounded-lg px-4 py-2 text-sm font-semibold"
              style={{ background: view === id ? '#6366f1' : 'transparent', color: '#fff', border: `1px solid ${view === id ? '#6366f1' : LINE}` }}>
              {label}
            </button>
          ))}
        </div>
        {view === 'trials' ? <TrialManagementView /> : <>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-lg font-semibold text-white">דופק ארגונים</h1>
          <div className="flex flex-wrap items-center gap-3 text-xs" style={{ color: MUTED }}>
            <span data-org-count={orgs.length}>{orgs.length} ארגונים</span>
            {query.data ? <span>{windowLabel(query.data)}</span> : null}
            <span>
              {query.dataUpdatedAt
                ? `עודכן ${formatDistanceToNow(query.dataUpdatedAt, { addSuffix: true, locale: he })}`
                : 'טוען'}
            </span>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeTest}
                onChange={(event) => setIncludeTest(event.target.checked)}
              />
              כולל ארגוני בדיקה
            </label>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1"
              style={{ border: `1px solid ${LINE}`, color: '#e5e7eb' }}
              onClick={() => { void query.refetch() }}
            >
              <RefreshCw size={14} className={query.isFetching ? 'animate-spin' : ''} />
              רענון
            </button>
          </div>
        </div>

        {query.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-indigo-500" />
          </div>
        ) : query.isError ? (
          <div className="rounded-xl p-6 text-center" style={{ border: `1px solid ${LINE}` }}>
            <p className="text-sm text-red-300">לא הצלחנו לטעון את הדופק. נסו לרענן.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-6" role="group" aria-label="סיכום">
              {kpis.map((kpi) => (
                <button
                  key={kpi.id}
                  type="button"
                  data-f={kpi.id}
                  aria-pressed={filter === kpi.id}
                  onClick={() => setFilter(kpi.id)}
                  className="grid gap-0.5 rounded-lg px-3 py-2.5 text-right"
                  style={{
                    background: '#1a1d27',
                    border: `1px solid ${kpi.tone === 'bad' ? '#f87171' : LINE}`,
                    outline: filter === kpi.id ? '2px solid #6366f1' : 'none',
                  }}
                >
                  <b
                    className="text-[22px] tabular-nums"
                    style={{ color: kpi.tone === 'bad' ? '#f87171' : kpi.tone === 'warn' ? '#fbbf24' : '#fff' }}
                  >
                    {kpi.n}
                  </b>
                  <span className="text-xs" style={{ color: MUTED }}>{kpi.label}</span>
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-1.5" role="group" aria-label="סינון">
              {FILTERS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  data-f={item.id}
                  aria-pressed={filter === item.id}
                  onClick={() => setFilter(item.id)}
                  className="rounded-full px-3 py-1 text-xs"
                  style={{
                    background: filter === item.id ? '#6366f1' : 'transparent',
                    border: `1px solid ${filter === item.id ? '#6366f1' : LINE}`,
                    color: filter === item.id ? '#fff' : '#e5e7eb',
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="w-full min-w-0 max-w-full overflow-x-auto">
              <table className="w-full border-collapse text-[13px] tabular-nums">
                <thead>
                  <tr>
                    <th className="w-1 p-0" />
                    {['ארגון', 'מסלול', 'נשאר', 'כניסה אחרונה', 'שינויים 24ש', 'פנייה אחרונה', 'מי מטפל'].map((label) => (
                      <th key={label} className="px-2 py-2 text-right text-xs font-medium" style={{ color: MUTED, borderBottom: `1px solid ${LINE}` }}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((org) => {
                    const state = STATE_LABEL[org.subscription_state] ?? { label: org.subscription_state, tone: 'idle' as const }
                    const handler = HANDLER_LABEL[org.handler] ?? { label: org.handler, tone: 'idle' as const }
                    const active = selected?.organization_id === org.organization_id
                    return (
                      <tr
                        key={org.organization_id}
                        tabIndex={0}
                        data-org-name={org.name}
                        data-severity={org.severity}
                        data-handler={org.handler}
                        data-human={org.human.total}
                        data-system={org.system.total}
                        data-state={org.subscription_state}
                        data-days={org.trial_days_left ?? ''}
                        className="cursor-pointer"
                        style={{ background: active ? '#1B2A48' : 'transparent' }}
                        onClick={() => setSelectedId(org.organization_id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') setSelectedId(org.organization_id)
                        }}
                      >
                        <td className="w-1 p-0" style={{ borderBottom: `1px solid ${LINE}` }}>
                          <i
                            className="block h-8 w-1 rounded-sm"
                            style={{ background: SEVERITY_BAR[org.severity] ?? SEVERITY_BAR.grey }}
                          />
                        </td>
                        <td className="px-2 py-2 text-right" style={{ borderBottom: `1px solid ${LINE}` }}>
                          <b className="text-white">{org.name}</b><OrgFreezeControl orgId={org.organization_id} />
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right" style={{ borderBottom: `1px solid ${LINE}` }}>
                          <Chip tone={state.tone}>{state.label}</Chip>
                          {' '}
                          <span style={{ color: MUTED }}>{planText(org)}</span>
                        </td>
                        <td className="px-2 py-2 text-right" style={{ borderBottom: `1px solid ${LINE}` }}>
                          <DaysLeft org={org} />
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right" style={{ borderBottom: `1px solid ${LINE}` }}>
                          {org.last_login_at
                            ? localStamp(org.last_login_at)
                            : <span className="font-semibold text-red-400">לא נכנס</span>}
                        </td>
                        <td className="px-2 py-2 text-right" style={{ borderBottom: `1px solid ${LINE}` }}>
                          <div className="grid gap-1">
                            <div>
                              <span className={org.human.total === 0 ? 'font-semibold text-red-400' : 'text-white'}>
                                אדם {org.human.total}
                              </span>
                              {' · '}
                              <span style={{ color: MUTED }}>מערכת {org.system.total}</span>
                            </div>
                            {org.human.total > 0 ? <ChangeChips counts={org.human} /> : null}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-right" style={{ color: MUTED, borderBottom: `1px solid ${LINE}` }}>
                          {outreachText(org.last_outreach)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right" style={{ borderBottom: `1px solid ${LINE}` }}>
                          <Chip tone={handler.tone}>{handler.label}</Chip>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {visible.length === 0 ? (
                <p className="py-8 text-center text-sm" style={{ color: MUTED }}>אין ארגונים בסינון הזה</p>
              ) : null}
            </div>

            {selected ? <PulseDrawer org={selected} /> : null}
          </>
        )}
        </>}
      </div>
    </div>
  )
}
